import { useCallback, useEffect, useRef, useState } from "react";

type GeolocationErrorCode = "permission_denied" | "unavailable" | "timeout" | "unsupported" | null;

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  loading: boolean;
  error: string | null;
  errorCode: GeolocationErrorCode;
}

type GeoSuccessCallback = (latitude: number, longitude: number) => void;

interface UseGeolocationReturn extends GeolocationState {
  request: (onSuccess?: GeoSuccessCallback) => void;
  cancel: () => void;
  permissionDenied: boolean;
}

const PERMISSION_DENIED_MESSAGE = "Allow location access, then try again";
const GEO_TIMEOUT_MS = 8_000;

function mapGeolocationError(err: GeolocationPositionError): {
  message: string;
  code: GeolocationErrorCode;
} {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return { message: PERMISSION_DENIED_MESSAGE, code: "permission_denied" };
    case err.POSITION_UNAVAILABLE:
      return {
        message: "Location unavailable. Try searching for an area.",
        code: "unavailable",
      };
    case err.TIMEOUT:
      return { message: "Location timed out. Try again.", code: "timeout" };
    default:
      return { message: "Could not get your location. Try again.", code: null };
  }
}

async function queryGeolocationPermission(): Promise<PermissionState | null> {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return null;
  }

  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state;
  } catch {
    return null;
  }
}

export function useGeolocation(): UseGeolocationReturn {
  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    loading: false,
    error: null,
    errorCode: null,
  });
  const isMounted = useRef(true);
  const requestId = useRef(0);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    requestId.current += 1;
    clearWatchdog();
    setState((prev) => ({ ...prev, loading: false, error: null, errorCode: null }));
  }, [clearWatchdog]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      requestId.current += 1;
      clearWatchdog();
    };
  }, [clearWatchdog]);

  const request = useCallback(
    (onSuccess?: GeoSuccessCallback) => {
      if (!isMounted.current) return;
      const id = ++requestId.current;
      const isCurrent = () => isMounted.current && requestId.current === id;
      const finish = (next: Partial<GeolocationState>) => {
        if (!isCurrent()) return false;
        requestId.current += 1;
        clearWatchdog();
        setState((prev) => ({ ...prev, ...next, loading: false }));
        return true;
      };
      clearWatchdog();

      if (typeof navigator === "undefined" || !navigator.geolocation) {
        finish({ error: "Geolocation is not supported", errorCode: "unsupported" });
        return;
      }

      setState((prev) => ({ ...prev, loading: true, error: null, errorCode: null }));
      void (async () => {
        const permission = await queryGeolocationPermission();
        if (!isCurrent()) return;
        if (permission === "denied") {
          finish({ error: PERMISSION_DENIED_MESSAGE, errorCode: "permission_denied" });
          return;
        }

        watchdogRef.current = window.setTimeout(() => {
          void (async () => {
            const permissionAfterWait = await queryGeolocationPermission();
            finish(
              permissionAfterWait === "denied"
                ? { error: PERMISSION_DENIED_MESSAGE, errorCode: "permission_denied" }
                : { error: "Location timed out. Try again.", errorCode: "timeout" },
            );
          })();
        }, GEO_TIMEOUT_MS);

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            if (finish({ latitude, longitude, error: null, errorCode: null })) {
              onSuccess?.(latitude, longitude);
            }
          },
          (err) => {
            const { message, code } = mapGeolocationError(err);
            finish({ error: message, errorCode: code });
          },
          { enableHighAccuracy: false, timeout: GEO_TIMEOUT_MS, maximumAge: 60_000 },
        );
      })();
    },
    [clearWatchdog],
  );

  return {
    ...state,
    request,
    cancel,
    permissionDenied: state.errorCode === "permission_denied",
  };
}

export { PERMISSION_DENIED_MESSAGE };
